import { useFacets, useProductFilters } from "@nosto/search-js/preact/hooks";
import TermsFacet from "./TermsFacet/TermsFacet";
import RangeFacet from "./RangeFacet/RangeFacet";
import { SearchStatsFacet, SearchTermsFacet } from "@nosto/nosto-js/client";
import { useFilterSidebar } from "@/contexts/SidebarContext";
import SelectedFilters from "../SelectedFilters/SelectedFilters";
import ClearFiltersButton from "../FilterTopbar/ClearFiltersButton/ClearFiltersButton";
import { useCallback, useEffect, useRef } from "preact/hooks";
import { cl } from "@nosto/search-js/utils";

type ToggleProps = {
  onClick?: () => void;
};

const globalLanguage = window?.Bedrock?.language;

function ToggleSidebarButton({ onClick }: ToggleProps = {}) {
  return (
    <button onClick={onClick}>
      <span className="sr-only">{globalLanguage?.close_filters}</span>
      <span
        className="br-icon"
        aria-hidden="true"
        style="--br-icon-color: black; color: black; --br-icon-width: 16px; --br-icon-height: 16px;"
      >
        <svg>
          <use href="#close"></use>
        </svg>
      </span>
    </button>
  );
}

export default function FilterSidebar() {
  const { facets } = useFacets();
  const { filters } = useProductFilters();
  const { isOpen, setOpen } = useFilterSidebar();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      document.body.style.overflow = "hidden";
    } else if (!isOpen && dialog.open) {
      dialog.close();
      document.body.style.overflow = "";
    }
    // Cleanup in case component unmounts while open
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === event.currentTarget) {
        setOpen(false);
      }
    },
    [setOpen],
  );

  if (facets?.length === 0) {
    return null;
  }

  return (
    <>
      <dialog
        ref={dialogRef}
        className="plp__mobile-filter-facets"
        onClose={handleClose}
        onClick={handleBackdropClick}
        aria-label="Filter Navigation"
      >
        <div className="mobile-filter-facets__header">
          <h3>Filters</h3>
          <ToggleSidebarButton onClick={() => setOpen(false)} />
        </div>

        {filters.length > 0 && (
          <div className="filter-facets__currently-selected">
            <SelectedFilters />
            <ClearFiltersButton />
          </div>
        )}

        <div className="mobile-filter-facets__nav">
          {facets?.map((facet) => {
            switch (facet.type) {
              case "terms":
                if (facet.data.length === 0) return <></>;
                return (
                  <TermsFacet
                    key={facet.id}
                    facet={facet as SearchTermsFacet}
                  />
                );
              case "stats":
                return (
                  <RangeFacet
                    key={facet.id}
                    facet={facet as SearchStatsFacet}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </dialog>
      <div className={cl("plp__mobile-filter-facets-cta", !isOpen && "hidden")}>
        <button
          className="br-button br-button--secondary br-button--handdrawn"
          onClick={() => setOpen(false)}
        >
          <span>{globalLanguage?.view_results}</span>
        </button>
      </div>
    </>
  );
}
